import type { ProjectTask, TaskPriority, TaskStatus } from '../types';

/** In-memory patches for Project Hub when scoped Firestore listeners lag CRM writes. */
export type TaskFieldOverride = {
  status?: TaskStatus;
  progress?: number;
  priority?: TaskPriority;
};

export function mergeTaskFieldOverride(
  task: ProjectTask,
  override: TaskFieldOverride | undefined,
): ProjectTask {
  if (!override) return task;
  return { ...task, ...override };
}

export function taskFieldOverrideMatchesLive(
  live: ProjectTask | undefined,
  override: TaskFieldOverride,
): boolean {
  if (!live) return false;
  if (override.status !== undefined && live.status !== override.status) return false;
  if (override.progress !== undefined && live.progress !== override.progress) return false;
  if (override.priority !== undefined && live.priority !== override.priority) return false;
  return true;
}

/** Drop overrides once scoped/live task rows reflect the patched fields. */
export function pruneTaskFieldOverrides(
  prev: Record<string, TaskFieldOverride>,
  liveTasks: Array<ProjectTask | undefined | null>,
): Record<string, TaskFieldOverride> {
  const ids = Object.keys(prev);
  if (ids.length === 0) return prev;
  const byId = new Map<string, ProjectTask>();
  for (const t of liveTasks) {
    if (t?.id) byId.set(t.id, t);
  }
  let changed = false;
  const next = { ...prev };
  for (const id of ids) {
    if (taskFieldOverrideMatchesLive(byId.get(id), next[id]!)) {
      delete next[id];
      changed = true;
    }
  }
  return changed ? next : prev;
}
